import { useNavigate } from 'react-router-dom';
import { config } from '../config';

import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, ExternalLink, ChevronLeft, ChevronRight, Newspaper } from 'lucide-react';
const NewsSection = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const scrollContainerRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const autoScrollRef = useRef(null);
  useEffect(() => {
    const getNewsData = async () => {
      try {
        const response = await fetch(`${config.API_HOST}/api/user/getNewsData`);
        if (response.ok) {
          const result = await response.json();
          setBlogPosts(result.data);
          console.log("setBlogPosts", result.data);
        } else {
          console.error("Failed to fetch news data");
        }
      } catch (err) {
        console.error("Error fetching news data:", err);
      }
    };

    getNewsData();
  }, []);

  // Check scroll position to update button states
const checkScrollPosition = () => {
  const container = scrollContainerRef.current;
  if (!container) return;

  setCanScrollLeft(container.scrollLeft > 0);
  setCanScrollRight(
    container.scrollLeft < container.scrollWidth - container.clientWidth
  );

  const cardWidth = container.children[0]?.offsetWidth || 400;
  const index = Math.round(container.scrollLeft / (cardWidth + 24));
  setCurrentIndex(index);
};


  // Scroll left function
  const scrollLeft = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({
        left: -400, // Scroll left by 400px
        behavior: 'smooth'
      });
    }
  };

  // Scroll right function
  const scrollRight = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({
        left: 400, // Scroll right by 400px
        behavior: 'smooth'
      });
    }
  };

  // Auto-scroll functionality (right to left)
useEffect(() => {
  if (!isAutoPlaying) return;

  const container = scrollContainerRef.current;
  if (!container || blogPosts.length === 0) return;

  const interval = setInterval(() => {
    if (container.scrollLeft >= container.scrollWidth - container.clientWidth) {
      container.scrollTo({ left: 0, behavior: 'smooth' });
      setCurrentIndex(0);
    } else {
      container.scrollBy({ left: 300, behavior: 'smooth' });
    }
  }, 3000);

  return () => clearInterval(interval);
}, [blogPosts, isAutoPlaying]);

const getCategoryColor = (category = '') => {
  switch (category.toLowerCase()) {
    case 'fintech':
      return 'from-cyan-500 to-emerald-500';
    case 'credit':
      return 'from-blue-500 to-indigo-500';
    case 'risk':
      return 'from-purple-500 to-pink-500';
    default:
      return 'from-slate-500 to-slate-600';
  }
};
const scrollToIndex = (index) => {
  const container = scrollContainerRef.current;
  if (!container) return;

  const cardWidth = container.children[0]?.offsetWidth || 400;
  container.scrollTo({
    left: index * (cardWidth + 24),
    behavior: 'smooth'
  });

  setCurrentIndex(index);
};

   return (
    <section id='news' className="py-24 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-20 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 glass-dark px-4 py-2 rounded-full text-sm font-semibold text-cyan-300 mb-6 border border-white/10">
            <Newspaper size={16} />
            <span className="mono">LATEST UPDATES</span>
          </div>
          
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="text-slate-200">Industry </span>
            <span className="text-gradient">News & Insights</span>
          </h2>
          
          <div className="h-1 w-24 bg-gradient-to-r from-cyan-500 to-emerald-500 mx-auto mb-8 rounded-full"></div>
          
          <p className="text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed">
            Stay informed with the latest trends and innovations in credit risk management and fintech.
          </p>
        </motion.div>
        
        {/* Carousel Container */}
        <div className="relative">
          {/* Navigation Buttons */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={scrollLeft}
            disabled={!canScrollLeft}
            className={`absolute left-0 top-1/2 transform -translate-y-1/2 z-20 glass-dark p-4 rounded-xl shadow-2xl border border-white/10 transition-all duration-300 ${
              canScrollLeft 
                ? 'hover:bg-white/10 text-cyan-400' 
                : 'text-slate-600 cursor-not-allowed opacity-40'
            }`}
          >
            <ChevronLeft size={24} />
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={scrollRight}
            disabled={!canScrollRight}
            className={`absolute right-0 top-1/2 transform -translate-y-1/2 z-20 glass-dark p-4 rounded-xl shadow-2xl border border-white/10 transition-all duration-300 ${
              canScrollRight 
                ? 'hover:bg-white/10 text-cyan-400' 
                : 'text-slate-600 cursor-not-allowed opacity-40'
            }`}
          >
            <ChevronRight size={24} />
          </motion.button>

          {/* Scrollable Cards Container */}
          <div 
            ref={scrollContainerRef}
            onScroll={checkScrollPosition}
            className="flex gap-6 overflow-x-auto scrollbar-hide px-12 py-6"
            style={{
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch'
            }}
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
          >
            {blogPosts.map((post, index) => (
              <motion.div 
                key={post.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex-shrink-0 w-96 group"
                style={{ scrollSnapAlign: 'start' }}
              >
                <div className="glass-dark rounded-2xl overflow-hidden border border-white/10 hover:bg-white/5 transition-all duration-500 h-full flex flex-col shadow-xl hover:shadow-2xl hover:shadow-cyan-500/20 transform hover:-translate-y-2">
                  {/* Image Container */}
                  <div className="relative overflow-hidden h-56 bg-gradient-to-br from-slate-800 to-slate-900">
                    {/* Category Badge */}
                    <div className={`absolute top-4 left-4 bg-gradient-to-r ${getCategoryColor(post.category)} text-white text-xs font-bold py-2 px-4 rounded-full z-10 shadow-lg mono`}>
                      {post.category}
                    </div>
                    
                    {/* Overlay gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent z-[1]"></div>
                    
                    {/* Image */}
                    <img 
                      src={`${config.API_HOST}${post.image}`} 
                      alt={post.title || 'News image'} 
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/400x300?text=SafeCredits';

                      }}
                    />
                    
                    {/* Decorative corner accent */}
                    <div className="absolute bottom-0 right-0 w-24 h-24 bg-gradient-to-tl from-cyan-500/20 to-transparent"></div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-6 flex-1 flex flex-col">
                    {/* Date */}
                    <div className="flex items-center gap-2 text-sm text-cyan-400 font-medium mb-3 mono">
                      <Calendar size={14} />
                      <span>{post.date}</span>
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-xl font-bold text-slate-100 mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-cyan-400 group-hover:to-emerald-400 transition-all duration-300 line-clamp-2 flex-1">
                      {post.title}
                    </h3>
                    
                    {/* Read More Link */}
                    <a 
                      href={post.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-cyan-400 font-semibold hover:gap-3 transition-all duration-300 group/link"
                    >
                      <span>Read Article</span>
                      <ExternalLink size={16} className="group-hover/link:rotate-12 transition-transform" />
                    </a>
                  </div>
                  
                  {/* Bottom accent line */}
                  <div className={`h-1 w-full bg-gradient-to-r ${getCategoryColor(post.category)} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500`}></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Pagination Dots */}
        <div className="flex justify-center items-center mt-12 gap-3">
          {blogPosts.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                scrollToIndex(index);
                setIsAutoPlaying(false);
              }}
              className="group relative"
            >
              <div className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? 'bg-gradient-to-r from-cyan-500 to-emerald-500 scale-110' 
                  : 'bg-slate-700 hover:bg-slate-600'
              }`}>
              </div>
              {index === currentIndex && (
                <div className="absolute inset-0 w-3 h-3 rounded-full bg-gradient-to-r from-cyan-500 to-emerald-500 blur-sm"></div>
              )}
            </button>
          ))}
        </div>

        {/* View All Button */}
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mt-16"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center gap-3 glass-dark px-8 py-4 rounded-xl font-semibold text-slate-200 hover:bg-white/10 transition-all border border-white/10 group"
          >
            <span>View All News</span>
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </motion.button>
        </motion.div>
      </div>
      
     <style>{`
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }

  .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .glass-dark {
    background: rgba(255, 255, 255, 0.03);
    backdrop-filter: blur(20px);
  }

  .text-gradient {
    background: linear-gradient(135deg, #60a5fa 0%, #a78bfa 50%, #f0abfc 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .mono {
    font-family: 'Space Mono', monospace;
  }

  @media (max-width: 768px) {
    .flex-shrink-0 {
      width: 320px;
    }
  }
`}</style>

    </section>
  );
}

export default NewsSection;